/**
 * @fileoverview Сессия смены: состояние + выделение + камера.
 */

import { MODE_KEY, bestScore, saveBest } from "../constants/world";
import { MODES, type ModeId } from "../constants/modes";
import {
  applyScenario,
  loadScenario,
  saveScenario,
  type ScenarioId,
} from "../constants/scenarios";
import { createGame } from "../state/createGame";
import { GameStore } from "../state/store";
import type {
  CameraState,
  GameHooks,
  GameState,
  Plane,
} from "../state/types";
import { tickSimulation } from "../systems/tick";

function loadMode(): ModeId {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m && m in MODES) return m as ModeId;
  } catch {
    /* игнор */
  }
  return "quick";
}

/**
 * Владеет `GameState` и UI-сессионными полями (selected / cam).
 * Симуляция вызывается через {@link Session.tick}.
 */
export class Session {
  /** Режим из меню */
  modeId: ModeId = loadMode();
  /** Сценарий старта */
  scenarioId: ScenarioId = loadScenario();
  /** Текущая смена (null до start) */
  store = new GameStore<GameState | null>(null);
  selected: Plane | null = null;
  cam: CameraState = {
    zoom: 1,
    panX: 0,
    panY: 0,
    dragging: false,
    lx: 0,
    ly: 0,
  };
  /** Последняя подпись фронта для HUD */
  frontLabel = "";

  get g(): GameState | null {
    return this.store.getState();
  }

  /** Сохранить выбранный режим. */
  setMode(id: ModeId): void {
    if (!(id in MODES)) return;
    this.modeId = id;
    try {
      localStorage.setItem(MODE_KEY, id);
    } catch {
      /* игнор */
    }
  }

  /** Выбрать сценарий старта. */
  setScenario(id: ScenarioId): void {
    this.scenarioId = id;
    saveScenario(id);
  }

  /** Лучший счет текущего режима. */
  best(): number {
    return bestScore(this.modeId);
  }

  /**
   * Начать смену (или туториал).
   * Сценарий применяется здесь; selectPlane из сценария — через hooks.
   */
  start(hooks: GameHooks, tutorial = false): GameState {
    const g = createGame(this.modeId, tutorial);
    this.store.replace(g);
    this.selected = null;
    this.cam = {
      zoom: 1,
      panX: 0,
      panY: 0,
      dragging: false,
      lx: 0,
      ly: 0,
    };
    if (!tutorial) applyScenario(g, hooks, this.scenarioId);
    return g;
  }

  /** Тик логики. */
  tick(hooks: GameHooks, dt: number): void {
    const g = this.g;
    if (!g) return;
    const res = tickSimulation(g, hooks, dt, this.selected);
    this.frontLabel = res.frontLabel;
    if (res.deselect) this.selected = null;
    this.store.replace(g);
    if (g.over && g.score > 0) {
      const b = bestScore(g.modeId);
      if (g.score > b) saveBest(g.score, g.modeId);
    }
  }

  select(p: Plane | null): void {
    this.selected = p;
  }
}
